import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react"

import { cn } from "@/lib/utils"


const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col p-4 sm:max-w-[420px]",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-xl border p-4 pr-10 shadow-lg backdrop-blur-xl transition-all duration-300 data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full max-w-sm",
  {
    variants: {
      variant: {
        default: "border-gray-200 bg-white/95 text-gray-900 shadow-gray-200/50",
        destructive:
          "border-red-200 bg-red-50/95 text-red-900 shadow-red-200/50",
        success:
          "border-green-200 bg-green-50/95 text-green-900 shadow-green-200/50",
        warning:
          "border-amber-200 bg-amber-50/95 text-amber-900 shadow-amber-200/50",
        info:
          "border-blue-200 bg-blue-50/95 text-blue-900 shadow-blue-200/50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  const getIcon = () => {
    switch (variant) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
      case "destructive":
        return <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
      case "info":
        return <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
      default:
        return <Info className="h-5 w-5 text-gray-600 flex-shrink-0" />
    }
  }

  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    >
      <div className="flex items-start space-x-3">
        {getIcon()}
        <div className="flex-1 min-w-0 pt-0.5">{children}</div>
      </div>
    </ToastPrimitives.Root>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-300",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-tight", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-80 leading-relaxed mt-1", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}

// Toast Usage Examples:
/*
// Basic usage with useToast hook
import { useToast } from '@/hooks/use-toast';

function MyComponent() {
  const { toast } = useToast();

  const handleClick = () => {
    toast({
      title: "Success!",
      description: "Operation completed successfully.",
      variant: "success"
    });
  };

  return <button onClick={handleClick}>Show Toast</button>;
}

// Using toast utilities for convenience
import { toastUtils } from '@/hooks/use-toast';

function MyComponent() {
  const handleSuccess = () => {
    toastUtils.success("Success!", "Operation completed successfully.");
  };

  const handleError = () => {
    toastUtils.error("Error", "Something went wrong.");
  };

  const handleWarning = () => {
    toastUtils.warning("Warning", "Please check your input.");
  };

  const handleInfo = () => {
    toastUtils.info("Info", "Here's some information.");
  };

  return (
    <div>
      <button onClick={handleSuccess}>Success</button>
      <button onClick={handleError}>Error</button>
      <button onClick={handleWarning}>Warning</button>
      <button onClick={handleInfo}>Info</button>
    </div>
  );
}

// Toast Showcase Component for testing and demonstration
export function ToastShowcase() {
  const { toast } = useToast()

  if (typeof window === 'undefined') {
    return null // Prevent SSR issues
  }

  const showAllToasts = () => {
    setTimeout(() => toastUtils.success("Success!", "Operation completed successfully."), 100);
    setTimeout(() => toastUtils.error("Error", "Something went wrong."), 1000);
    setTimeout(() => toastUtils.warning("Warning", "Please check your input."), 2000);
    setTimeout(() => toastUtils.info("Info", "Here's some information."), 3000);
    setTimeout(() => toastUtils.default("Default", "This is a default toast."), 4000);
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-lg font-semibold">Toast Showcase</h3>
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => toastUtils.success("Success!", "Operation completed successfully.")}
          className="p-2 bg-green-500 text-white rounded"
        >
          Success Toast
        </button>
        <button
          onClick={() => toastUtils.error("Error", "Something went wrong.")}
          className="p-2 bg-red-500 text-white rounded"
        >
          Error Toast
        </button>
        <button
          onClick={() => toastUtils.warning("Warning", "Please check your input.")}
          className="p-2 bg-yellow-500 text-white rounded"
        >
          Warning Toast
        </button>
        <button
          onClick={() => toastUtils.info("Info", "Here's some information.")}
          className="p-2 bg-blue-500 text-white rounded"
        >
          Info Toast
        </button>
        <button
          onClick={() => toastUtils.default("Default", "This is a default toast.")}
          className="p-2 bg-gray-500 text-white rounded"
        >
          Default Toast
        </button>
        <button
          onClick={showAllToasts}
          className="p-2 bg-purple-500 text-white rounded col-span-2"
        >
          Show All Toasts
        </button>
      </div>
    </div>
  );
}
*/